/**
 * StepEquipmentSelect.jsx
 *
 * Tool Finder REPLACE path — combined Rig / Rock Drill selector with
 * SearchableDropdown cascades. Replaces 6 legacy card-based steps
 * (StepRigType / Family / Model + StepDrillType / Family / Model) with a
 * single tabbed screen.
 *
 * PORTED from TH ToolFinder v4:
 *   `tophammer-toolfinder-v4/src/components/wizard/StepEquipmentSelect.jsx`
 *   (540 lines). The inline SearchableDropdown (lines 38-199) was previously
 *   extracted as `@components/ui/SearchableDropdown` (SP3.A.2 +
 *   SP3.A.2-enhance) and is consumed here via the enhanced primitive's
 *   `primaryField` / `secondaryField` / `renderBadges` props. The rest of
 *   this component is a verbatim port of TF v4 lines 200-540.
 *
 * MRT adaptations (documented in the SP3.A.3 brief):
 *   1. SearchableDropdown is imported, not inlined. Item shape is the
 *      enhanced two-line variant: rigs render
 *        primary  = productName
 *        secondary = "{manufacturer} · {category} · {minMm}-{maxMm}mm"
 *      drills render
 *        primary  = model name
 *        secondary = manufacturer
 *      manufacturers render plain `label` (no secondary).
 *   2. Data comes from `useProducts()` (ProductContext), not props.
 *      TF v4 passed rigs / rockDrills / rockDrillManufacturers / tabCounts /
 *      visibleTabs as props from App.jsx — MRT centralises them.
 *   3. Wizard handlers come from `useWizard()`:
 *        handleEquipmentSelectRig(mfr, rig)
 *        handleEquipmentSelectDrill(mfr, modelName, detailedDrill)
 *        handleEquipmentSelectFallback()
 *        handlePreviousExplore() — reused as Back from EQUIPMENT_SELECT.
 *   4. Icons: lucide-react (`Search`, `ArrowRight`, `ArrowLeft`, `Info`,
 *      `Settings`, `Wrench`, `ChevronDown`, `ChevronUp`). TF v4 used
 *      Material Symbols Outlined — MRT depends on lucide-react ^0.542.0
 *      already (via SearchableDropdown). `Wrench` stands in for TF v4's
 *      `precision_manufacturing` (no exact lucide equivalent).
 *   5. Sandvik design tokens: electric-blue, eb-1, n1–n9, carbon-black.
 *      Hex map applied:
 *        #1441F5 → electric-blue   #C8DCFF → eb-1
 *        #F8F8F8 → n1              #E6E6E6 → n2
 *        #9D9D9D → n4              #575757 → n6
 *        #101010 → carbon-black
 *   6. No rounded corners (Sandvik brand).
 *
 * @module components/wizard/StepEquipmentSelect
 * @since 2026-05-18 (Group D Sub-Plan 3, Task SP3.A.3)
 */

import { useState, useMemo, useCallback } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Info,
  Settings,
  Wrench,
} from 'lucide-react';
import SearchableDropdown from '@components/ui/SearchableDropdown';
import { useWizard } from '@contexts/WizardContext';
import { useProducts } from '@contexts/ProductContext';

// ============================================================================
// TAB BAR — Sandvik style, electric-blue active state, no rounded corners.
// Ported from TF v4 StepEquipmentSelect.jsx lines 204-226.
// ============================================================================
function TabBar({ tabs, activeTab, onTabChange }) {
  return (
    <div className="flex border-b-2 border-n2" role="tablist">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        const TabIcon = tab.icon;
        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            data-testid={`tab-${tab.id}`}
            onClick={() => onTabChange(tab.id)}
            className={[
              'flex-1 px-4 py-3 font-sandvik-text font-normal text-sm text-center',
              'transition-colors relative focus:outline-none',
              isActive
                ? 'text-electric-blue border-b-2 border-electric-blue'
                : 'text-n6 hover:text-carbon-black hover:bg-n1',
            ].join(' ')}
            style={isActive ? { borderBottomWidth: '3px', marginBottom: '-2px' } : {}}
          >
            <span className="flex items-center justify-center gap-2">
              <TabIcon size={18} aria-hidden="true" />
              {tab.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// CATEGORY PREVIEW — product counts per visible result tab.
// Ported from TF v4 StepEquipmentSelect.jsx lines 230-254.
// ============================================================================
function CategoryPreview({ tabCounts = {}, visibleTabs = [] }) {
  if (!visibleTabs || visibleTabs.length === 0) return null;
  return (
    <div className="mt-6" data-testid="category-preview">
      <p className="text-xs font-sandvik-text text-n4 mb-2 tracking-wide">
        Available products
      </p>
      <div className="flex flex-wrap gap-2">
        {visibleTabs.slice(0, 6).map((tab) => (
          <div
            key={tab.key}
            className="px-3 py-2 bg-n1 border border-n2"
          >
            <span className="font-sandvik-text text-xs text-n6">{tab.label}</span>
            <span className="ml-2 font-sandvik-text text-xs font-normal text-electric-blue">
              {tabCounts[tab.key] || 0}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const TABS = [
  { id: 'rig',       label: 'Have a rig',              icon: Wrench   },
  { id: 'rockDrill', label: 'Rock Drill — Top hammer', icon: Settings },
];

/**
 * StepEquipmentSelect — Tool Finder Replace path entry component.
 * Renders the tabbed Rig / Rock Drill picker with manufacturer + model
 * SearchableDropdown cascades.
 */
export default function StepEquipmentSelect() {
  const { selections, handleEquipmentSelectRig, handleEquipmentSelectDrill,
    handleEquipmentSelectFallback, setCurrentStep } = useWizard();
  const {
    rigsData,
    rockDrillsData,
    rockDrillManufacturersData,
    tabCounts,
    visibleTabs,
  } = useProducts();

  // Default tab is whatever WizardContext.selections.replaceTab dictates
  // ('rig' | 'rockDrill'); stays local thereafter.
  const [activeTab, setActiveTab] = useState(selections?.replaceTab || 'rig');

  // ---- Rig tab state ----
  const [rigManufacturer, setRigManufacturer] = useState(null);
  const [rigModel, setRigModel] = useState(null);

  // ---- Rock Drill tab state ----
  const [drillManufacturer, setDrillManufacturer] = useState(null);
  const [drillModel, setDrillModel] = useState(null);

  // Switching tabs clears BOTH sides' picks (TF v4 lines 285-291).
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    setRigManufacturer(null);
    setRigModel(null);
    setDrillManufacturer(null);
    setDrillModel(null);
  }, []);

  // ========================================================================
  // RIG TAB — manufacturer / model items
  // ========================================================================
  const rigManufacturerItems = useMemo(() => {
    if (!rigsData) return [];
    const allRigs = [
      ...(rigsData.surfaceRigs || []),
      ...(rigsData.undergroundRigs || []),
    ];
    const mfgs = [...new Set(allRigs.map((r) => r.manufacturer).filter(Boolean))];
    return mfgs.sort().map((m) => ({ id: m.toLowerCase(), label: m }));
  }, [rigsData]);

  const rigModelItems = useMemo(() => {
    if (!rigsData || !rigManufacturer) return [];
    const allRigs = [
      ...(rigsData.surfaceRigs || []),
      ...(rigsData.undergroundRigs || []),
    ];
    return allRigs
      .filter((r) => r.manufacturer === rigManufacturer.label)
      .map((r) => {
        const hd = r.holeDiameter;
        const range = hd?.minMm && hd?.maxMm ? `${hd.minMm}-${hd.maxMm}mm` : null;
        const secondaryParts = [
          r.manufacturer,
          r.category || r.rigType,
          range,
        ].filter(Boolean);
        return {
          ...r,
          id: r.rigId,
          // `label` kept as the TF v4 display string for back-compat with
          // SearchableDropdown's default `displayField`. The primary/secondary
          // pair drives the visual rendering.
          label: `${r.productName}${r.rigType ? ` : ${r.rigType}` : ''}`,
          searchLabel: r.productName,
          primaryText: r.productName,
          secondaryText: secondaryParts.join(' · '),
        };
      })
      .sort((a, b) => a.productName.localeCompare(b.productName));
  }, [rigsData, rigManufacturer]);

  // Rig info panel text (TF v4 lines 343-352).
  const rigInfoText = useMemo(() => {
    if (!rigModel) return null;
    const hd = rigModel.holeDiameter;
    if (hd?.minMm && hd?.maxMm) {
      return `This ${rigModel.productName} ${rigModel.category || 'Top Hammer'} rig is typically used for hole sizes between ${hd.minMm} to ${hd.maxMm} mm`;
    }
    return `Selected: ${rigModel.productName}`;
  }, [rigModel]);

  // ========================================================================
  // ROCK DRILL TAB — manufacturer / model items
  // ========================================================================
  const drillManufacturerItems = useMemo(() => {
    if (!rockDrillManufacturersData?.manufacturers) return [];
    return rockDrillManufacturersData.manufacturers
      .map((m) => ({ id: m.id, label: m.name, modelCount: m.modelCount }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rockDrillManufacturersData]);

  const drillModelItems = useMemo(() => {
    if (!rockDrillManufacturersData?.manufacturers || !drillManufacturer) return [];
    const mfgData = rockDrillManufacturersData.manufacturers.find(
      (m) => m.id === drillManufacturer.id,
    );
    if (!mfgData) return [];
    return mfgData.models
      .map((model) => ({
        id: `${drillManufacturer.id}-${model}`,
        label: model,
        manufacturer: drillManufacturer.label,
        primaryText: model,
        secondaryText: drillManufacturer.label,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rockDrillManufacturersData, drillManufacturer]);

  // ========================================================================
  // HANDLERS
  // ========================================================================
  const handleRigManufacturerChange = useCallback((item) => {
    setRigManufacturer(item);
    setRigModel(null);
  }, []);

  const handleDrillManufacturerChange = useCallback((item) => {
    setDrillManufacturer(item);
    setDrillModel(null);
  }, []);

  const handleNext = useCallback(() => {
    if (activeTab === 'rig' && rigModel) {
      handleEquipmentSelectRig(rigManufacturer.label, rigModel);
    } else if (activeTab === 'rockDrill' && drillModel) {
      // Sandvik drill → look up detailed object in rockDrills.json.
      // Competitor drill → pass null detailedDrill (TF v4 lines 304-316).
      let detailedDrill = null;
      if (drillManufacturer.label === 'Sandvik' && rockDrillsData) {
        const allDrills = [
          ...(rockDrillsData.rockDrills?.surface || []),
          ...(rockDrillsData.rockDrills?.underground || []),
        ];
        detailedDrill = allDrills.find((d) => d.model === drillModel.label) || null;
      }
      handleEquipmentSelectDrill(drillManufacturer.label, drillModel.label, detailedDrill);
    }
  }, [
    activeTab, rigManufacturer, rigModel, drillManufacturer, drillModel,
    rockDrillsData, handleEquipmentSelectRig, handleEquipmentSelectDrill,
  ]);

  const handleBack = useCallback(() => {
    // Replace path lands here from ENTRY directly; the Back button returns
    // to the entry-point card. Mirror legacy behaviour by clearing entryPath
    // is not necessary — selections retain replaceTab but currentStep moves
    // back. WizardContext doesn't expose a dedicated handlePreviousReplace;
    // we set currentStep to ENTRY explicitly (matches TF v4 onBack contract).
    setCurrentStep('ENTRY');
  }, [setCurrentStep]);

  const canProceed = activeTab === 'rig' ? !!rigModel : !!drillModel;

  // ========================================================================
  // RENDER
  // ========================================================================
  return (
    <section
      aria-labelledby="equipment-select-heading"
      className="max-w-2xl mx-auto px-4 py-6"
    >
      {/* Header */}
      <header className="mb-6">
        <h1
          id="equipment-select-heading"
          className="font-sandvik-headline font-normal text-2xl md:text-3xl text-carbon-black mb-2"
        >
          Select your equipment
        </h1>
        <p className="font-sandvik-text text-n6">
          Choose your rig or rock drill to find compatible Sandvik rock tools
        </p>
      </header>

      <TabBar tabs={TABS} activeTab={activeTab} onTabChange={handleTabChange} />

      <div className="mt-6 space-y-5">
        {activeTab === 'rig' && (
          <>
            <SearchableDropdown
              label="Rig Manufacturer"
              placeholder="Select manufacturer..."
              items={rigManufacturerItems}
              value={rigManufacturer}
              onChange={handleRigManufacturerChange}
              displayField="label"
              primaryField="label"
            />

            <SearchableDropdown
              label="Model"
              placeholder={rigManufacturer ? 'Search or select model...' : 'Select manufacturer first'}
              items={rigModelItems}
              value={rigModel}
              onChange={setRigModel}
              disabled={!rigManufacturer}
              displayField="label"
              searchFields={['label', 'searchLabel', 'productName']}
              primaryField="primaryText"
              secondaryField="secondaryText"
            />

            {rigInfoText && (
              <div
                role="note"
                data-testid="rig-info"
                className="flex items-start gap-3 p-4 bg-eb-1/30 border-l-4 border-electric-blue"
              >
                <Info size={20} className="text-electric-blue flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="font-sandvik-text text-sm text-carbon-black">{rigInfoText}</p>
              </div>
            )}

            <div className="pt-1">
              <button
                type="button"
                data-testid="rig-fallback-link"
                onClick={handleEquipmentSelectFallback}
                className="font-sandvik-text text-sm text-electric-blue hover:underline flex items-center gap-1"
              >
                Can&apos;t find rig model?
                <ArrowRight size={14} aria-hidden="true" />
              </button>
            </div>

            {rigModel && (
              <CategoryPreview tabCounts={tabCounts} visibleTabs={visibleTabs} />
            )}
          </>
        )}

        {activeTab === 'rockDrill' && (
          <>
            <SearchableDropdown
              label="Rock Drill Manufacturer"
              placeholder="Select manufacturer..."
              items={drillManufacturerItems}
              value={drillManufacturer}
              onChange={handleDrillManufacturerChange}
              displayField="label"
              primaryField="label"
            />

            <SearchableDropdown
              label="Model"
              placeholder={drillManufacturer ? 'Search or select model...' : 'Select manufacturer first'}
              items={drillModelItems}
              value={drillModel}
              onChange={setDrillModel}
              disabled={!drillManufacturer}
              displayField="label"
              primaryField="primaryText"
              secondaryField="secondaryText"
            />

            <div className="pt-1">
              <button
                type="button"
                data-testid="drill-fallback-link"
                onClick={handleEquipmentSelectFallback}
                className="font-sandvik-text text-sm text-electric-blue hover:underline flex items-center gap-1"
              >
                Can&apos;t find rock drill / model?
                <ArrowRight size={14} aria-hidden="true" />
              </button>
            </div>

            {drillModel && (
              <CategoryPreview tabCounts={tabCounts} visibleTabs={visibleTabs} />
            )}
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-8 pt-6 border-t border-n2">
        <button
          type="button"
          data-testid="back-button"
          onClick={handleBack}
          className="flex items-center gap-2 px-4 py-3 font-sandvik-text font-normal text-n6 hover:text-carbon-black transition-colors"
        >
          <ArrowLeft size={18} aria-hidden="true" />
          Back
        </button>

        <button
          type="button"
          data-testid="next-button"
          onClick={handleNext}
          disabled={!canProceed}
          className={[
            'flex items-center gap-2 px-8 py-3',
            'font-sandvik-text font-normal transition-colors',
            canProceed
              ? 'bg-electric-blue text-white hover:bg-carbon-black'
              : 'bg-n4 text-white cursor-not-allowed',
          ].join(' ')}
        >
          Next
          <ArrowRight size={18} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}
